import { useState } from 'react';
import { useTranslations } from 'use-intl';
import type { ObjectiveInput } from '@shared/schemas';
import type { Locale } from '@/i18n';
import { localePath } from '@/app/locale';
import {
  useCreateObjective,
  useObjectiveChain,
  useObjectives,
  useSeasons,
  useTeams,
  useUpdateObjectiveStatus,
} from '@/app/queries';
import { formatDate } from '@/lib/format';
import { Button, Spinner } from '@/ui/Button';
import { Field, SelectInput, TextArea, TextInput } from '@/ui/Field';
import { EmptyState, PageHeader, Panel } from '@/ui/Panel';
import { Section } from '@/ui/Section';
import { Chip } from '@/ui/Chip';

const LEVELS = ['season', 'development', 'cycle', 'microcycle', 'session', 'exercise'] as const;
const STATUSES = ['open', 'active', 'achieved', 'dropped'] as const;

const LEVEL_STYLE: Record<string, string> = {
  season: 'bg-load-rest/12 text-load-rest',
  development: 'bg-load-rest/12 text-load-rest',
  cycle: 'bg-load-low/12 text-load-low',
  microcycle: 'bg-load-moderate/14 text-load-moderate',
  session: 'bg-load-high/12 text-load-high',
  exercise: 'bg-load-peak/12 text-load-peak',
};

/**
 * The WHY panel.
 *
 * Walks an objective back to where it came from. When the root was promoted from
 * a match problem it says so, which is the difference between a coach saying
 * "we're working on progression" and being able to point at the fixture that
 * made it the priority.
 */
function WhyChain({ objectiveId, locale }: { objectiveId: string; locale: Locale }) {
  const t = useTranslations();
  const chain = useObjectiveChain(objectiveId);

  if (chain.isLoading) {
    return (
      <p className="flex items-center gap-2 px-4 py-3 text-meta text-ink-muted">
        <Spinner /> {t('common.loading')}
      </p>
    );
  }

  const rows = chain.data?.chain ?? [];
  const source = chain.data?.source;

  return (
    <div className="border-t border-line bg-surface-sunken px-4 py-4">
      <h4 className="font-condensed font-semibold">{t('why.chain')}</h4>
      <ol className="mt-3 grid gap-1.5">
        {rows.map((row, i) => (
          <li
            key={row.id}
            className="flex items-baseline gap-2"
            style={{ paddingInlineStart: `${i * 1.25}rem` }}
          >
            {i > 0 ? <span className="text-ink-faint">└</span> : null}
            <span
              className={`rounded-[3px] px-1.5 py-0.5 text-[0.6875rem] ${
                LEVEL_STYLE[row.level] ?? 'bg-surface text-ink-muted'
              }`}
            >
              {t(`why.level_${row.level}`)}
            </span>
            <span className="min-w-0">{row.title}</span>
          </li>
        ))}
      </ol>
      {source?.kind === 'match_problem' && source.event ? (
        <p
          className="mt-3 border-s-2 border-load-peak ps-3 text-meta text-ink-muted"
          style={{ marginInlineStart: `${rows.length * 1.25}rem` }}
        >
          {t('why.fromMatch', {
            opponent: source.event.opponent ?? t('calendar.kind_match'),
            date: formatDate(source.event.onDate, locale),
          })}
          {source.problem?.detail ? (
            <span className="mt-1 block text-ink-faint">{source.problem.detail}</span>
          ) : null}
        </p>
      ) : null}
      {chain.data?.children?.length ? (
        <p className="mt-3 text-meta text-ink-faint">
          {chain.data.children.map((c) => c.title).join(' · ')}
        </p>
      ) : null}
    </div>
  );
}

export function Objectives({ locale }: { locale: Locale }) {
  const t = useTranslations();
  const teams = useTeams();
  const teamList = teams.data ?? [];
  const [teamId, setTeamId] = useState<string | null>(null);
  const activeTeamId = teamId ?? teamList[0]?.id ?? null;

  const seasons = useSeasons(activeTeamId ?? undefined);
  const objectives = useObjectives(activeTeamId ?? undefined);
  const create = useCreateObjective();
  const setStatus = useUpdateObjectiveStatus();

  const [expanded, setExpanded] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<ObjectiveInput>({
    level: 'season',
    title: '',
    emphasis: 'primary',
    status: 'open',
  } as ObjectiveInput);

  const list = objectives.data ?? [];

  return (
    <>
      <PageHeader
        title={t('why.objectives')}
        explain="objective_chain"
        count={list.length || undefined}
        lead={t('sessions.rationaleHint')}
        help={localePath(locale, '/guide/what')}
        action={
          <Button variant="primary" onClick={() => setOpen((v) => !v)}>
            {t('why.addObjective')}
          </Button>
        }
      />

      {teamList.length > 1 ? (
        <div className="mb-4 flex flex-wrap gap-1">
          {teamList.map((tm) => (
            <button
              key={tm.id}
              type="button"
              onClick={() => setTeamId(tm.id)}
              aria-pressed={tm.id === activeTeamId}
              className={`rounded-(--radius-control) border px-3 py-1.5 text-meta transition-colors ${
                tm.id === activeTeamId
                  ? 'border-ink bg-ink text-paper'
                  : 'border-line bg-surface text-ink-muted hover:border-ink'
              }`}
            >
              {tm.name}
            </button>
          ))}
        </div>
      ) : null}

      {open ? (
        <div className="mb-5">
          <Panel title={t('why.addObjective')}>
            <Section title={t('why.objectives')}>
              <div className="grid gap-x-5 gap-y-2 sm:grid-cols-3">
                <Field label={t('why.title')}>
                  {(p) => (
                    <SelectInput
                      {...p}
                      value={form.level}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, level: e.target.value as typeof f.level }))
                      }
                    >
                      {LEVELS.map((l) => (
                        <option key={l} value={l}>
                          {t(`why.level_${l}`)}
                        </option>
                      ))}
                    </SelectInput>
                  )}
                </Field>
                <div className="sm:col-span-2">
                  <Field label={t('exercises.objectiveText')}>
                    {(p) => (
                      <TextInput
                        {...p}
                        value={form.title}
                        onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                        required
                      />
                    )}
                  </Field>
                </div>
                <div className="sm:col-span-3">
                  <Field label={t('exercises.description')} optional>
                    {(p) => (
                      <TextArea
                        {...p}
                        value={form.detail ?? ''}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, detail: e.target.value || undefined }))
                        }
                      />
                    )}
                  </Field>
                </div>
              </div>
            </Section>
            <div className="flex gap-2 border-t border-line px-4 py-4">
              <Button
                variant="primary"
                busy={create.isPending}
                disabled={!form.title.trim()}
                onClick={async () => {
                  await create.mutateAsync({
                    ...form,
                    teamId: activeTeamId ?? undefined,
                    seasonId: seasons.data?.[0]?.id,
                  });
                  setForm({ level: 'season', title: '', emphasis: 'primary', status: 'open' } as ObjectiveInput);
                  setOpen(false);
                }}
              >
                {t('teams.save')}
              </Button>
              <Button variant="quiet" onClick={() => setOpen(false)}>
                {t('common.cancel')}
              </Button>
            </div>
          </Panel>
        </div>
      ) : null}

      <Panel>
        {objectives.isLoading ? (
          <p className="flex items-center gap-2 px-4 py-8 text-ink-muted">
            <Spinner /> {t('common.loading')}
          </p>
        ) : list.length === 0 ? (
          <EmptyState title={t('why.noObjectives')} body={t('why.noObjectivesBody')} />
        ) : (
          <ul className="divide-y divide-line">
            {list.map((o) => (
              <li key={o.id}>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3">
                  <button
                    type="button"
                    onClick={() => setExpanded(expanded === o.id ? null : o.id)}
                    aria-expanded={expanded === o.id}
                    className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1 text-start"
                  >
                    <span
                      className={`rounded-[3px] px-1.5 py-0.5 text-meta ${
                        LEVEL_STYLE[o.level] ?? 'bg-surface-sunken text-ink-muted'
                      }`}
                    >
                      {t(`why.level_${o.level}`)}
                    </span>
                    <span className="min-w-0 flex-1 font-medium">{o.title}</span>
                    {o.sourceEntity === 'match_problem' ? (
                      <Chip muted>{t('calendar.kind_match')}</Chip>
                    ) : null}
                  </button>
                  <SelectInput
                    value={o.status}
                    aria-label={t('seasons.status')}
                    className="!min-h-11 w-32 !text-meta"
                    onChange={(e) =>
                      void setStatus.mutateAsync({ objectiveId: o.id, status: e.target.value })
                    }
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {t(`why.status_${s}`)}
                      </option>
                    ))}
                  </SelectInput>
                </div>
                {o.detail ? (
                  <p className="px-4 pb-3 text-meta text-ink-muted">{o.detail}</p>
                ) : null}
                {expanded === o.id ? <WhyChain objectiveId={o.id} locale={locale} /> : null}
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </>
  );
}
